import { useState, useEffect, useCallback, useRef } from 'react';
import { offerStoreAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Upload, Download, Loader2, Boxes } from 'lucide-react';
import { AddOfferModal } from '../components/offer-store/AddOfferModal';
import { UploadOffersModal } from '../components/offer-store/UploadOffersModal';
import { OfferDetailModal } from '../components/offer-store/OfferDetailModal';

export default function OfferStorePage() {
  const { canAccess } = useAuth();
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [availability, setAvailability] = useState('all');
  const [source, setSource] = useState('all');
  const [notInCatalogue, setNotInCatalogue] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const canCreate = canAccess('OFFER_STORE', 'CREATE');
  const canImport = canAccess('OFFER_STORE', 'IMPORT');
  const canExport = canAccess('OFFER_STORE', 'EXPORT');

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (qDebounced) params.q = qDebounced;
    if (availability !== 'all') params.availability = availability;
    if (source !== 'all') params.source = source;
    if (notInCatalogue) params.not_in_catalogue = true;
    return params;
  }, [qDebounced, availability, source, notInCatalogue]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await offerStoreAPI.list({ ...buildFilterParams(), page });
      setOffers(r.data.offers || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [page, buildFilterParams]);

  useEffect(() => { fetchData(); }, [fetchData]);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setPage(1);
  }, [qDebounced, availability, source, notInCatalogue]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await offerStoreAPI.export(buildFilterParams());
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'offer_store_export.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export offers');
    } finally {
      setExporting(false);
    }
  };

  const availabilityBadge = (o) => o.availability === 'LEAD_TIME'
    ? <Badge variant="outline" className="rounded-sm bg-amber-50 text-amber-700 text-xs">Lead time {o.lead_time_days != null ? `${o.lead_time_days}d` : ''}</Badge>
    : <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-700 text-xs">Ready stock</Badge>;

  return (
    <div className="space-y-6" data-testid="offer-store-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Boxes className="h-6 w-6" />Offer Store</h1>
          <p className="text-sm text-muted-foreground mt-1">What vendors have offered us — including models not in our catalogue.</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button
              variant="outline" className="rounded-sm" data-testid="offer-store-export-btn"
              onClick={handleExport} disabled={exporting}
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export
            </Button>
          )}
          {canImport && (
            <Button variant="outline" className="rounded-sm" onClick={() => setUploadOpen(true)} data-testid="offer-store-upload-btn">
              <Upload className="h-4 w-4 mr-2" />Upload sheet
            </Button>
          )}
          {canCreate && (
            <Button className="rounded-sm" onClick={() => setAddOpen(true)} data-testid="offer-store-add-btn">
              <Plus className="h-4 w-4 mr-2" />Add offer
            </Button>
          )}
        </div>
      </div>

      <Card className="rounded-sm"><CardContent className="p-4">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search model..." className="pl-10 rounded-sm" data-testid="offer-store-search-input" />
          </div>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="offer-store-availability-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All availability</SelectItem>
              <SelectItem value="READY_STOCK">Ready stock</SelectItem>
              <SelectItem value="LEAD_TIME">Lead time</SelectItem>
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="offer-store-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="MANUAL">Manual</SelectItem>
              <SelectItem value="EXCEL">Excel upload</SelectItem>
              <SelectItem value="BID_ENGINE">Bid Engine</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" className="h-4 w-4" checked={notInCatalogue} onChange={e => setNotInCatalogue(e.target.checked)} data-testid="offer-store-not-in-catalogue-checkbox" />
            Not in catalogue
          </label>
        </div>
      </CardContent></Card>

      <Card className="rounded-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead>Price (USD)</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Availability</TableHead>
              <TableHead>Offer date</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Entered by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : offers.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No offers found</TableCell></TableRow>
            ) : offers.map(o => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedId(o.id)} data-testid={`offer-row-${o.id}`}>
                <TableCell className="font-medium">
                  {o.model_raw}
                  {!o.matched_sku && <Badge variant="outline" className="ml-2 rounded-sm text-xs bg-slate-50 text-slate-600">Not in catalogue</Badge>}
                </TableCell>
                <TableCell>{o.price != null ? `$${Number(o.price).toFixed(2)}` : '—'}</TableCell>
                <TableCell>{o.qty ?? '—'}</TableCell>
                <TableCell>{o.vendor_name_raw}</TableCell>
                <TableCell>{o.vendor_country || '—'}</TableCell>
                <TableCell>{availabilityBadge(o)}</TableCell>
                <TableCell>{o.offer_date}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm text-xs">{o.source}</Badge></TableCell>
                <TableCell>{o.created_by_name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {totalPages > 1 ? (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} offer(s) total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="offer-store-prev-page">Previous</Button>
            <span className="px-2 py-1">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="offer-store-next-page">Next</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{total} offer(s) total</p>
      )}

      {addOpen && <AddOfferModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); fetchData(); }} />}
      {uploadOpen && <UploadOffersModal open={uploadOpen} onClose={() => setUploadOpen(false)} onCommitted={() => { setUploadOpen(false); fetchData(); }} />}
      {selectedId && <OfferDetailModal offerId={selectedId} onClose={() => setSelectedId(null)} onChanged={fetchData} />}
    </div>
  );
}
